import React from 'react';
import { Save, Send, Check } from 'lucide-react';
import Button from './Button';
import Tip from './Tip';

export type SaveTarget = 'draft' | 'submit' | 'approve';

interface WorkflowSaveBarProps {
  /** User holds modify extent — may save a draft. */
  canDraft: boolean;
  /** User may submit for approval. */
  canSubmit: boolean;
  /** User may approve directly (self-approval allowed). */
  canApprove: boolean;
  busy?: boolean;
  disabled?: boolean;
  /** Called with the chosen target state. Parent validates + persists. */
  onSave: (target: SaveTarget) => void;
  /** Optional label overrides (per-module wording). */
  labels?: Partial<Record<SaveTarget, string>>;
}

/**
 * Common CRUD-form footer for RBAC workflow. Renders compact action icons (with
 * hover tooltips) for the express save choices — Save as Draft / Submit /
 * Approve — gated by the caller's permission booleans. After a record exists,
 * further transitions happen via the row's ApprovalDot → ApprovalWorkflowModal,
 * not here.
 *
 * Used across every GRC module so the maker-checker save UX is identical.
 */
const WorkflowSaveBar: React.FC<WorkflowSaveBarProps> = ({
  canDraft, canSubmit, canApprove, busy = false, disabled = false, onSave, labels,
}) => {
  const off = busy || disabled;
  const L = {
    draft: labels?.draft ?? 'Save as Draft',
    submit: labels?.submit ?? 'Submit for Approval',
    approve: labels?.approve ?? 'Save & Approve',
  };

  if (!canDraft && !canSubmit && !canApprove) return null;

  return (
    <div className="flex items-center gap-2" data-testid="workflow-save-bar">
      {canDraft && (
        <Tip text={L.draft} placement="top">
          <Button
            variant="outline"
            icon={Save}
            onClick={() => onSave('draft')}
            disabled={off}
            aria-label={L.draft}
            data-testid="BTN_wf_save_draft"
          />
        </Tip>
      )}
      {canSubmit && (
        <Tip text={L.submit} placement="top">
          <Button
            variant="fill"
            icon={Send}
            onClick={() => onSave('submit')}
            disabled={off}
            aria-label={L.submit}
            data-testid="BTN_wf_save_submit"
          />
        </Tip>
      )}
      {canApprove && (
        <Tip text={L.approve} placement="top">
          <Button
            variant="active-outline"
            icon={Check}
            onClick={() => onSave('approve')}
            disabled={off}
            aria-label={L.approve}
            data-testid="BTN_wf_save_approve"
          />
        </Tip>
      )}
    </div>
  );
};

export default WorkflowSaveBar;
